/**
 * Where a store stands on its plan — the rules the billing screens read
 * off the billing status payload and the `/billing/pricing/` payload.
 *
 * No i18n, no formatters, no UI kit. Arguments are plain wire values, and
 * results are values or KINDs the caller maps onto its own copy.
 */

// States in which "your subscription is active, next charge on X" is a TRUE
// statement. An allowlist, not `!needs_subscription`, because access and
// payment are different questions — billing/domain/states.py grants access
// in states that are emphatically not "paid and running":
//
//   GRACE            — "Granted regardless of payment state".
//   PENDING_APPROVAL — a subscribe was pushed but never completed a paid period.
//   BETA / INTERNAL  — the complimentary tiers: "always granted, never billed".
//
// PENDING_CHANGE is included because it IS paid through today; the pending
// change itself is stated separately.
const SUBSCRIBED_STATES = ['active', 'pending_change'];

/** Is this store paying for a subscription right now? */
export function isSubscribed(status) {
  return SUBSCRIBED_STATES.includes(status);
}

// States in which the merchant may CHANGE their plan. A superset of
// SUBSCRIBED_STATES: a platform-trial merchant has APPROVED a real
// subscription, so there is a plan to switch, and switching costs nothing
// (every push carries the remaining evaluation days). The backend switch
// accepts the same three states (billing/services/switching.py); the two
// lists moving together is what keeps a rendered button from being a 409.
const PLAN_CHANGE_STATES = [...SUBSCRIBED_STATES, 'platform_trial'];

/** May this merchant switch interval or move band? */
export function canChangePlan(status) {
  return PLAN_CHANGE_STATES.includes(status);
}

// States with access and nothing approved to change: the evaluation window
// (`trial`) and an explicit grace. Subscribing from the Plans page is how
// such a merchant pays before the window or the grace closes. Any state
// with an approved subscription is excluded — a second subscribe would
// replace a charge the merchant already agreed to.
const SUBSCRIBE_STATES = ['trial', 'grace'];

/** May this merchant start a subscription while they still have access? */
export function canSubscribe(status) {
  return SUBSCRIBE_STATES.includes(status);
}

/**
 * The one state where the plan and the billing genuinely differ: a quoted
 * store whose active subscription is still the old plan. Null otherwise.
 *
 * The old plan's next charge rides along, so the money still to come is on
 * the screen.
 *
 * @param {{status: string, tier: string, planName: string, paidUntil: string|null}} billing
 * @param {object|null} pricing
 */
export function quotedPendingMove({ status, tier, planName, paidUntil }, pricing) {
  if (!pricing?.enterprise_offer) return null;
  if (!isSubscribed(status)) return null;
  if (tier === 'enterprise') return null;
  return {
    monthly: pricing.enterprise_offer.monthly,
    billedPlan: planName,
    nextChargeDate: paidUntil ?? null,
  };
}

/** How the marked card became the merchant's plan. */
export const TIER_EXPLAINER = {
  /** A sales agreement, not a measurement. */
  QUOTED: 'quoted',
  /** Banded from a full year of revenue. */
  REVENUE: 'revenue',
  /** Under a year of history IS the Core plan, not a pending answer. */
  UNDER_A_YEAR: 'under_a_year',
};

/** @param {object} pricing the `/billing/pricing/` payload */
export function tierExplainer(pricing) {
  if (pricing?.enterprise_offer) return TIER_EXPLAINER.QUOTED;
  return pricing?.has_12_months ? TIER_EXPLAINER.REVENUE : TIER_EXPLAINER.UNDER_A_YEAR;
}
